"use client";

import mixpanel from "mixpanel-browser";
import {formatMixpanelEventName} from "./mixpanel-format-event-name";
import {isConfirmedBot, type BotReason} from "./is-bot";
import {getGaClientIdFromBrowserCookie} from "../../gtm/lib/ga-client-id-client";

let APP_EVENT_PREFIX: string = "";

let initPromise: Promise<void> | null = null;
let isInitialized = false;

export interface InitMixpanelOptions {
	// prefix prepended to all tracked event names
	eventPrefix?: string;
	recordSessionsPercent?: number;
	// Computed server-side (Next's `next/server` user-agent utilities aren't
	// available in client code) and passed in, rather than detected here.
	// `known_bot`/`automation_tool` still skip init entirely; a
	// `missing_user_agent` reason is shadow mode — tagged as a `possible_bot`
	// property but doesn't skip init. See isConfirmedBot's doc comment.
	botReason?: BotReason | null;
	// Identifies anonymous visitors with the same GA4 client id every
	// server-tracked event already uses (see `getGaClientId`), instead of
	// leaving the SDK's own randomly-generated `$device:` id in place —
	// otherwise autocapture and our custom funnel events land under two
	// unrelated anonymous Mixpanel profiles for the same real visitor. Opt-in
	// per app, not the default: other apps using this shared package haven't
	// been verified against this change.
	identifyWithGaClientId?: boolean;
	// Mixpanel autocapture pageview. Default `"full-url"` matches current
	// apps. Discovery turns this off and calls `track_pageview` itself so
	// `$mp_web_page_view` gets the current route's `event_key` instead of
	// a leftover super property from the previous page.
	autocapturePageview?: false | "full-url";
}

/**
 * Retries reading the `_ga` cookie for a few seconds before giving up —
 * covers the common case where `init()` runs before GTM/GA's own async
 * script has had a chance to write it yet (first pageview of a session, or
 * any page load that's simply faster than that network round trip). Safe to
 * call `identify()` late: it retroactively merges the anonymous profile with
 * whatever events already fired under it, so there's no need to block init
 * on this resolving.
 */
function identifyOnceGaClientIdAvailable(attemptsLeft = 20): void {
	const gaClientId = getGaClientIdFromBrowserCookie();
	if (gaClientId) {
		mixpanel.identify(gaClientId);
		return;
	}
	if (attemptsLeft <= 1) {
		return;
	}
	setTimeout(() => identifyOnceGaClientIdAvailable(attemptsLeft - 1), 250);
}

/**
 * Initialize Mixpanel for client-side tracking.
 * Uses NEXT_PUBLIC_MIXPANEL_TOKEN and NEXT_PUBLIC_APP_NAME from environment variables.
 */
export function initMixpanelClient({
	eventPrefix = "",
	recordSessionsPercent = 50, // session replay sampling rate is on 50% by default
	botReason = null,
	identifyWithGaClientId = false,
	autocapturePageview = "full-url",
}: InitMixpanelOptions = {}): Promise<void> {
	// Update the prefix on every call that provides one, even if init already
	// started elsewhere — otherwise an earlier prefix-less caller (e.g. some
	// other mounted component auto-initializing via getMixpanelClient/
	// waitForMixpanel before this app's own MixpanelInit effect runs)
	// permanently locks events to the wrong name for the rest of the page's
	// life, since the early return below skips every call after the first.
	if (eventPrefix) {
		APP_EVENT_PREFIX = eventPrefix;
	}

	// Return existing promise if initialization is in progress or complete
	if (initPromise) {
		return initPromise;
	}

	const app = process.env.NEXT_PUBLIC_APP_NAME;
	if (!app) {
		console.warn(
			"NEXT_PUBLIC_APP_NAME not set. Set it in next.config.js to identify the app in Mixpanel."
		);
	}

	if (isConfirmedBot(botReason)) {
		initPromise = Promise.resolve();
		return initPromise;
	}

	initPromise = new Promise((resolve) => {
		try {
			mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!, {
				autocapture: {
					pageview: autocapturePageview,
					click: false,
					input: false,
					scroll: false,
					submit: true,
					capture_text_content: false,
				},
				persistence: "localStorage",
				stop_utm_persistence: true, // Prevent Mixpanel from persisting UTM parameters across sessions
				batch_requests: false, // Send events immediately instead of batching
				batch_size: 1, // Send events one at a time
				record_sessions_percent: recordSessionsPercent, // Session replay sampling rate, can be overridden
			});

			if (identifyWithGaClientId) {
				identifyOnceGaClientIdAvailable();
			}

			mixpanel.register({
				app: app || "unknown",
				user_agent: navigator.userAgent,
				possible_bot: botReason,
			});
			// Drop leftover page event_key from localStorage so it cannot leak
			// onto later client events in other same-origin apps.
			mixpanel.unregister("event_key");

			isInitialized = true;
			resolve();
		} catch (error) {
			console.error("Failed to initialize Mixpanel:", error);
			resolve(); // Resolve anyway to not block
		}
	});

	return initPromise;
}

/**
 * Wait for Mixpanel to be initialized.
 * Auto-initializes if not already started.
 * Returns a promise that resolves when Mixpanel is ready.
 */
export function waitForMixpanel(): Promise<void> {
	// Auto-initialize if not already started
	if (!initPromise) {
		return initMixpanelClient();
	}
	return initPromise;
}

/**
 * Get the Mixpanel instance for client-side tracking.
 * Waits for initialization if not yet ready. Returns null if init was
 * skipped (confirmed bot traffic) — mixpanel-browser throws internally if
 * you call methods on it before `.init()` has run.
 */
export async function getMixpanelClient() {
	await waitForMixpanel();
	return isInitialized ? mixpanel : null;
}

/**
 * Return the current Mixpanel distinct ID from the client SDK.
 */
export async function getMixpanelDistinctId(): Promise<string | null> {
	try {
		const mp = await getMixpanelClient();
		if (!mp) {
			return null;
		}
		const distinctId = mp.get_distinct_id();
		if (!distinctId) {
			console.error(
				"Mixpanel distinct ID is unavailable. Skipping server tracking."
			);
			return null;
		}
		return distinctId;
	} catch (error) {
		console.error(
			"Failed to get Mixpanel distinct ID. Skipping server tracking:",
			error
		);
		return null;
	}
}

/**
 * Get the Mixpanel instance synchronously if already initialized.
 * Returns null if not yet initialized.
 *
 * Useful for event handlers where async operations may not complete before
 * the browser terminates the page (e.g., pagehide, beforeunload).
 */
export function getMixpanelClientSync() {
	return isInitialized ? mixpanel : null;
}

/**
 * Track a Mixpanel event with automatic initialization and error handling.
 * Use this for imperative tracking in event handlers.
 *
 * @example
 * await trackEvent("Button Clicked", { buttonId: "submit" });
 */
export async function trackEvent(
	event: string,
	properties?: Record<string, unknown>
) {
	try {
		const mp = await getMixpanelClient();
		if (!mp) {
			return;
		}
		mp.track(formatMixpanelEventName(event, APP_EVENT_PREFIX), properties);
	} catch (error) {
		console.error("Failed to track Mixpanel event:", error);
	}
}

/**
 * Track a Mixpanel event synchronously. Does nothing if Mixpanel is not initialized.
 * Use this for pagehide/beforeunload handlers where async operations may not complete.
 *
 * @example
 * trackEventSync("Page Exited", { secondsOnPage: 30 }, { transport: "sendBeacon" });
 */
export function trackEventSync(
	event: string,
	properties?: Record<string, unknown>,
	options?: {transport?: "sendBeacon" | "xhr"}
): void {
	const mp = getMixpanelClientSync();
	if (!mp) {
		return;
	}
	try {
		mp.track(
			formatMixpanelEventName(event, APP_EVENT_PREFIX),
			properties,
			options
		);
	} catch (error) {
		console.error("Failed to track Mixpanel event:", error);
	}
}
